/**
 * Grid System for Cell Swap Game
 * 
 * Manages a 6x12 grid (6 columns, 12 rows) representing the stem cell playfield.
 * Handles cell placement, removal, gravity simulation, and neighbor detection.
 */

import { Cell, CellType, CellState, CellUtils } from './CellTypes'

export interface GridPosition {
  x: number
  y: number
}

export interface MatchGroup {
  cells: Cell[]
  type: CellType
  size: number
}

export class Grid {
  public static readonly WIDTH = 6
  public static readonly HEIGHT = 12
  private cells: (Cell | null)[][]
  private animatingCells: Set<string>

  constructor() {
    this.cells = this.createEmptyGrid()
    this.animatingCells = new Set()
  }

  private createEmptyGrid(): (Cell | null)[][] {
    const grid: (Cell | null)[][] = []
    for (let y = 0; y < Grid.HEIGHT; y++) {
      grid[y] = new Array(Grid.WIDTH).fill(null)
    }
    return grid
  }

  /**
   * Places a cell at the specified position
   */
  public placeCell(cell: Cell, x: number, y: number): boolean {
    if (!this.isValidPosition(x, y) || this.cells[y][x] !== null) {
      return false
    }

    cell.x = x
    cell.y = y
    this.cells[y][x] = cell
    return true
  }

  /**
   * Removes a cell from the specified position
   */
  public removeCell(x: number, y: number): Cell | null {
    if (!this.isValidPosition(x, y)) {
      return null
    }

    const cell = this.cells[y][x]
    this.cells[y][x] = null
    return cell
  }

  /**
   * Gets the cell at the specified position
   */
  public getCell(x: number, y: number): Cell | null {
    if (!this.isValidPosition(x, y)) {
      return null
    }
    return this.cells[y][x]
  }

  /**
   * Checks if a position is valid within the grid bounds
   */
  public isValidPosition(x: number, y: number): boolean {
    return x >= 0 && x < Grid.WIDTH && y >= 0 && y < Grid.HEIGHT
  }

  /**
   * Checks if a position is empty (no cell)
   */
  public isEmpty(x: number, y: number): boolean {
    return this.isValidPosition(x, y) && this.cells[y][x] === null
  }

  /**
   * Applies gravity to all cells, making them fall down
   */
  public applyGravity(): boolean {
    let cellsMoved = false

    // Process from bottom to top, left to right
    for (let x = 0; x < Grid.WIDTH; x++) {
      for (let y = Grid.HEIGHT - 2; y >= 0; y--) {
        const cell = this.cells[y][x]
        if (cell !== null) {
          let newY = y

          // Find the lowest empty position
          while (newY + 1 < Grid.HEIGHT && this.cells[newY + 1][x] === null) {
            newY++
          }

          // Move the cell if it can fall
          if (newY !== y) {
            this.cells[y][x] = null
            this.cells[newY][x] = cell
            cell.y = newY
            cell.state = CellState.FALLING
            cellsMoved = true
          }
        }
      }
    }

    return cellsMoved
  }

  /**
   * Gets all neighboring cells (4-directional)
   */
  public getNeighbors(x: number, y: number): Cell[] {
    const neighbors: Cell[] = []
    const directions = [
      { dx: 0, dy: -1 }, // up
      { dx: 1, dy: 0 },  // right
      { dx: 0, dy: 1 },  // down
      { dx: -1, dy: 0 }  // left
    ]

    for (const dir of directions) {
      const nx = x + dir.dx
      const ny = y + dir.dy
      const cell = this.getCell(nx, ny)
      if (cell !== null) {
        neighbors.push(cell)
      }
    }

    return neighbors
  }

  /**
   * Finds all connected cells of the same type using flood fill
   */
  public findConnectedCells(startX: number, startY: number): Cell[] {
    const startCell = this.getCell(startX, startY)
    if (!startCell || !CellUtils.getProperties(startCell.type).canBeMatched) {
      return []
    }

    const visited = new Set<string>()
    const connected: Cell[] = []
    const queue: GridPosition[] = [{ x: startX, y: startY }]

    while (queue.length > 0) {
      const pos = queue.shift()!
      const key = `${pos.x},${pos.y}`

      if (visited.has(key)) {
        continue
      }

      visited.add(key)
      const cell = this.getCell(pos.x, pos.y)

      if (cell && CellUtils.canMatch(startCell.type, cell.type)) {
        connected.push(cell)

        // Add neighbors to queue
        const neighbors = [
          { x: pos.x, y: pos.y - 1 },
          { x: pos.x + 1, y: pos.y },
          { x: pos.x, y: pos.y + 1 },
          { x: pos.x - 1, y: pos.y }
        ]

        for (const neighbor of neighbors) {
          if (this.isValidPosition(neighbor.x, neighbor.y) &&
            !visited.has(`${neighbor.x},${neighbor.y}`)) {
            queue.push(neighbor)
          }
        }
      }
    }

    return connected
  }

  /**
   * Finds all match groups of 4+ connected cells
   */
  public findMatches(): MatchGroup[] {
    const matches: MatchGroup[] = []
    const processed = new Set<string>()

    for (let y = 0; y < Grid.HEIGHT; y++) {
      for (let x = 0; x < Grid.WIDTH; x++) {
        const cell = this.getCell(x, y)
        const key = `${x},${y}`

        if (cell && !processed.has(key) &&
          CellUtils.getProperties(cell.type).canBeMatched) {

          const connected = this.findConnectedCells(x, y)

          if (connected.length >= 4) {
            matches.push({
              cells: connected,
              type: cell.type,
              size: connected.length
            })

            // Mark these cells as processed
            for (const connectedCell of connected) {
              processed.add(`${connectedCell.x},${connectedCell.y}`)
            }
          }
        }
      }
    }

    return matches
  }

  /**
   * Marks cells for deletion and sets their state
   */
  public markCellsForDeletion(cells: Cell[]): void {
    for (const cell of cells) {
      cell.markedForDeletion = true
      cell.state = CellState.MATCHED
    }
  }

  /**
   * Removes all cells marked for deletion
   */
  public removeMarkedCells(): Cell[] {
    const removedCells: Cell[] = []

    for (let y = 0; y < Grid.HEIGHT; y++) {
      for (let x = 0; x < Grid.WIDTH; x++) {
        const cell = this.cells[y][x]
        if (cell && cell.markedForDeletion) {
          this.cells[y][x] = null
          removedCells.push(cell)
        }
      }
    }

    return removedCells
  }

  /**
   * Checks if the grid is overflowing (cells in the top row)
   */
  public isOverflowing(): boolean {
    for (let x = 0; x < Grid.WIDTH; x++) {
      if (this.cells[0][x] !== null) {
        return true
      }
    }
    return false
  }

  /**
   * Legacy method for cancer cell clearing (not used in Puyo mode)
   */
  public clearCancerCells(): Cell[] {
    // In Puyo mode, no special cancer clearing logic
    return []
  }

  /**
   * Gets all cells in the grid
   */
  public getAllCells(): Cell[] {
    const allCells: Cell[] = []
    for (let y = 0; y < Grid.HEIGHT; y++) {
      for (let x = 0; x < Grid.WIDTH; x++) {
        const cell = this.cells[y][x]
        if (cell) {
          allCells.push(cell)
        }
      }
    }
    return allCells
  }

  /**
   * Counts cells of a specific type
   */
  public countCellsOfType(type: CellType): number {
    let count = 0
    for (let y = 0; y < Grid.HEIGHT; y++) {
      for (let x = 0; x < Grid.WIDTH; x++) {
        const cell = this.cells[y][x]
        if (cell && cell.type === type) {
          count++
        }
      }
    }
    return count
  }

  /**
   * Clears the entire grid
   */
  public clear(): void {
    this.cells = this.createEmptyGrid()
    this.animatingCells.clear()
  }

  /**
   * Gets a copy of the grid state for debugging
   */
  public getGridState(): (CellType | null)[][] {
    return this.cells.map(row =>
      row.map(cell => cell ? cell.type : null)
    )
  }

  // ========================================
  // Dr. Mario Capsule-Aware Gravity Methods
  // ========================================

  /**
   * Gets the capsule partner cell (the other half of the capsule)
   */
  public getCapsulePartner(cell: Cell): Cell | null {
    if (!cell.capsulePartner) {
      return null
    }

    // Search for the partner cell by ID
    for (let y = 0; y < Grid.HEIGHT; y++) {
      for (let x = 0; x < Grid.WIDTH; x++) {
        const candidate = this.cells[y][x]
        if (candidate && candidate.id === cell.capsulePartner) {
          return candidate
        }
      }
    }

    return null
  }

  /**
   * Breaks the capsule connection between two cells
   */
  public breakCapsuleConnection(cell: Cell): void {
    const partner = this.getCapsulePartner(cell)

    if (partner) {
      // Clear the connection in both directions
      partner.capsulePairId = undefined
      partner.capsulePartner = undefined
    }

    cell.capsulePairId = undefined
    cell.capsulePartner = undefined
  }

  /**
   * Applies gravity with capsule-aware movement (for Dr. Mario mode)
   * Capsules stay connected and move together as a unit
   * Cancer cells never move (they are stationary viruses)
   */
  public applyGravityWithCapsules(): boolean {
    let cellsMoved = false
    const processedCells = new Set<string>()

    // Process from bottom to top to ensure proper falling order
    for (let y = Grid.HEIGHT - 2; y >= 0; y--) {
      for (let x = 0; x < Grid.WIDTH; x++) {
        const cell = this.cells[y][x]

        if (!cell || processedCells.has(cell.id)) {
          continue
        }

        // Cancer cells are stationary and never fall
        if (cell.type === CellType.CANCER) {
          processedCells.add(cell.id)
          continue
        }

        // Check if this cell is part of a capsule
        if (cell.capsulePairId && cell.capsulePartner) {
          // Move the entire capsule as a unit
          if (this.moveCapsulePair(cell)) {
            cellsMoved = true
            processedCells.add(cell.id)

            const partner = this.getCapsulePartner(cell)
            if (partner) {
              processedCells.add(partner.id)
            }
          }
        } else {
          // Move independent cell (no capsule connection)
          if (this.moveIndependentCell(cell)) {
            cellsMoved = true
            processedCells.add(cell.id)
          }
        }
      }
    }

    return cellsMoved
  }

  /**
   * Moves a capsule pair (two connected cells) as a unit
   */
  private moveCapsulePair(cell: Cell): boolean {
    const partner = this.getCapsulePartner(cell)

    if (!partner) {
      // Partner is missing, break connection and move independently
      this.breakCapsuleConnection(cell)
      return this.moveIndependentCell(cell)
    }

    // Determine capsule orientation (horizontal or vertical)
    const isHorizontal = cell.y === partner.y

    if (isHorizontal) {
      return this.moveHorizontalCapsule(cell, partner)
    } else {
      return this.moveVerticalCapsule(cell, partner)
    }
  }

  /**
   * Moves a horizontal capsule (side-by-side cells)
   */
  private moveHorizontalCapsule(cell1: Cell, cell2: Cell): boolean {
    // Both cells must be able to move down
    const canCell1MoveDown = cell1.y < Grid.HEIGHT - 1 && !this.cells[cell1.y + 1][cell1.x]
    const canCell2MoveDown = cell2.y < Grid.HEIGHT - 1 && !this.cells[cell2.y + 1][cell2.x]

    if (canCell1MoveDown && canCell2MoveDown) {
      // Move both cells down together
      this.cells[cell1.y][cell1.x] = null
      this.cells[cell2.y][cell2.x] = null

      cell1.y++
      cell2.y++

      this.cells[cell1.y][cell1.x] = cell1
      this.cells[cell2.y][cell2.x] = cell2

      return true
    }

    return false
  }

  /**
   * Moves a vertical capsule (stacked cells)
   */
  private moveVerticalCapsule(cell1: Cell, cell2: Cell): boolean {
    // Determine which cell is on top and which is on bottom
    const topCell = cell1.y < cell2.y ? cell1 : cell2
    const bottomCell = cell1.y < cell2.y ? cell2 : cell1

    // Only the bottom cell needs clearance to move
    const canMoveDown = bottomCell.y < Grid.HEIGHT - 1 && !this.cells[bottomCell.y + 1][bottomCell.x]

    if (canMoveDown) {
      // Move both cells down together
      this.cells[topCell.y][topCell.x] = null
      this.cells[bottomCell.y][bottomCell.x] = null

      topCell.y++
      bottomCell.y++

      this.cells[topCell.y][topCell.x] = topCell
      this.cells[bottomCell.y][bottomCell.x] = bottomCell

      return true
    }

    return false
  }

  /**
   * Moves an independent cell (not part of a capsule)
   */
  private moveIndependentCell(cell: Cell): boolean {
    // Check if cell can move down
    if (cell.y >= Grid.HEIGHT - 1) {
      return false // At bottom
    }

    const cellBelow = this.cells[cell.y + 1][cell.x]
    if (cellBelow) {
      return false // Blocked
    }

    // Move cell down
    this.cells[cell.y][cell.x] = null
    cell.y++
    this.cells[cell.y][cell.x] = cell

    return true
  }
}